<template>
  <div
    v-if="alertReady"
    class="alert"
  >
    <div
      v-if="immediate.urgency || immediate.statement"
      class="immediate"
    >
      <span class="immediate-icon">
        <font-awesome-icon
          icon="exclamation-triangle"
          size="2x"
          class="actions-urgency"
        />
      </span>
      <span class="immediate-text">
        <template v-if="immediate.statement">
          {{ immediate.statement }}
        </template>
        <template v-else-if="immediate.urgency">
          <span
            :class="{
              term: true,
              ['term-inverse']: true,
              ['term-selected']: term === 'response',
              ['term-selected-inverse']: term === 'response'
            }"
            @click="toggleTerm('response')"
          >{{ immediate.response }}</span><!-- eslint-disable-line vue/multiline-html-element-content-newline -->
          {{ immediate.urgency }}
        </template>
      </span>
    </div>
    <div
      class="title"
      :style="styles.title"
    >
      <div class="event">
        {{ alert.details.event }}
      </div>
      <div class="timely">
        {{ timely }}
      </div>
      <div
        v-if="status"
        class="status"
      >
        {{ status.start }}
        <template v-if="status.severity">
          <span
            :class="{
              term: true,
              ['term-selected']: term === 'severity'
            }"
            @click="toggleTerm('severity')"
          >{{ status.severity }}</span><!-- eslint-disable-line vue/multiline-html-element-content-newline -->
        </template>
        event
        <template v-if="status.certainty">
          that {{ status.transition }}
          <span
            :class="{
              term: true,
              ['term-selected']: term === 'certainty'
            }"
            @click="toggleTerm('certainty')"
          >{{ status.certainty }}</span><!-- eslint-disable-line vue/multiline-html-element-content-newline -->
        </template>
        <template v-if="status.end">
          {{ status.end }}
        </template>
      </div>
    </div>
    <div class="body">
      <div
        v-if="term"
        class="terms"
      >
        <div class="advanced-buttons terms-buttons">
          <div
            class="advanced-button"
            @click="toggleTerm(null)"
          >
            <font-awesome-icon
              icon="chevron-left"
            />
            Return to Alert
          </div>
        </div>
        <p>The selected term relates to <i>{{ term }}</i> and may be defined as:</p>
        <div v-if="term === 'certainty'">
          <div class="term-grid">
            <div class="term-name">
              Observed
            </div>
            <div class="term-value">
              Determined to have occurred or to be ongoing
            </div>
            <div class="term-name">
              Likely
            </div>
            <div class="term-value">
              More than 50% probability
            </div>
            <div class="term-name">
              Possible
            </div>
            <div class="term-value">
              Less than 50% probability
            </div>
            <div class="term-name">
              Unlikely
            </div>
            <div class="term-value">
              Not expected to occur
            </div>
            <div class="term-name">
              Unknown
            </div>
            <div class="term-value">
              Certainty unknown or not provided
            </div>
          </div>
        </div>
        <div v-if="term === 'severity'">
          <div class="term-grid">
            <div class="term-name">
              Extreme
            </div>
            <div class="term-value">
              Extraordinary threat to life or property
            </div>
            <div class="term-name">
              Severe
            </div>
            <div class="term-value">
              Significant threat to life or property
            </div>
            <div class="term-name">
              Moderate
            </div>
            <div class="term-value">
              Possible threat to life or property
            </div>
            <div class="term-name">
              Minor
            </div>
            <div class="term-value">
              Minimal to no known threat to life or property
            </div>
            <div class="term-name">
              Unknown
            </div>
            <div class="term-value">
              Severity unknown or not provided
            </div>
          </div>
        </div>
        <div v-if="term === 'response'">
          <div class="term-grid">
            <div class="term-name">
              Shelter
            </div>
            <div class="term-value">
              Take shelter in place or per the instructions
            </div>
            <div class="term-name">
              Evacuate
            </div>
            <div class="term-value">
              Relocate per the instructions
            </div>
            <div class="term-name">
              Prepare
            </div>
            <div class="term-value">
              Make preparations per the instructions
            </div>
            <div class="term-name">
              Execute
            </div>
            <div class="term-value">
              Execute a pre-planned activity identified in the instructions
            </div>
            <div class="term-name">
              Avoid
            </div>
            <div class="term-value">
              Avoid the event area per the instructions
            </div>
            <div class="term-name">
              Monitor
            </div>
            <div class="term-value">
              Attend to information sources as described in instructions
            </div>
            <div class="term-name">
              AllClear
            </div>
            <div class="term-value">
              Event is no longer a threat, follow on action is described in instructions
            </div>
            <div class="term-name">
              None
            </div>
            <div class="term-value">
              No action recommended
            </div>
          </div>
        </div>
        <p>
          Refer to the
          <a
            href="https://vlab.noaa.gov/web/nws-common-alerting-protocol/cap-documentation"
            target="_blank"
          >
            alert specification</a><!-- eslint-disable-line vue/multiline-html-element-content-newline -->
          for more details.
        </p>
      </div>
      <div
        v-else
        class="details"
      >
        <div
          v-if="alert.details.description"
          class="preformatted description"
        ><!-- eslint-disable-line vue/multiline-html-element-content-newline -->{{ prettyPrint(alert.details.description) }}
        </div>
        <div
          v-if="alert.details.instruction"
          class="preformatted instruction"
        ><!-- eslint-disable-line vue/multiline-html-element-content-newline -->{{ prettyPrint(alert.details.instruction) }}
        </div>
        <div class="sender">
          Issued by <a :href="`mailto:${alert.details.sender}`">{{ alert.details.senderName }}</a>
        </div>
      </div>
      <div
        v-if="term === null"
        class="terms-help"
      >
        Tip! Click <span class="term">underlined terms</span> above to learn what they respresent.
      </div>
    </div>
    <div class="advanced">
      <div>Advanced Options</div>
      <div class="advanced-buttons">
        <div
          class="advanced-button"
          @click="toggleAdvancedPanel('properties')"
        >
          Properties
          <span class="immediate-icon">
            <font-awesome-icon
              icon="angle-down"
              size="2x"
              :class="{ 'rotate-180': advancedPanel === 'properties' }"
            />
          </span>
        </div>
        <div
          class="advanced-button advanced-button-gap"
          @click="openInApi(alert.details.url)"
        >
          <span class="immediate-icon">
            <font-awesome-icon
              icon="hockey-puck"
              size="2x"
            />
          </span>
          View in API
        </div>
      </div>
      <div
        v-if="advancedPanel === 'properties'"
        class="properties"
      >
        <div class="properties-instruction">
          Click on a property name to copy its value to the clipboard.
        </div>
        <div
          v-for="item in properties"
          :key="item"
          class="preformatted"
        >
          <strong
            class="properties-name"
            @click="copyTextToClipboard(alert.details[item], capitalizeFirstLetter(item))"
          >{{ capitalizeFirstLetter(item) }}</strong><!-- eslint-disable-line vue/multiline-html-element-content-newline -->
          <div
            v-if="alert.details[item] && alert.details[item] !== null"
            class="properties-value truncate-single-line"
          >{{ typeof alert.details[item] === 'object' ? JSON.stringify(alert.details[item], null, 2) : alert.details[item] }}</div><!-- eslint-disable-line vue/multiline-html-element-content-newline -->
          <div
            v-else
            class="properties-value"
          >
            (not provided)
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="no-alert"
  >
    No alert selected.
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faExclamationTriangle, faAngleDown, faHockeyPuck, faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { DateTime } from 'luxon'

library.add(faExclamationTriangle, faAngleDown, faHockeyPuck, faChevronLeft)

export default {
  components: {
    FontAwesomeIcon
  },
  data () {
    return {
      styles: {
        title: {}
      },
      isActive: false,
      advancedPanel: null,
      properties: [
        'id',
        'event',
        'eventCode',
        'headline',
        'vtec',
        'sent',
        'effective',
        'onset',
        'expires',
        'ends',
        'areaDesc',
        'sender',
        'senderName',
        'category',
        'certainty',
        'severity',
        'urgency',
        'response',
        'status',
        'messageType'
      ],
      term: null
    }
  },
  computed: {
    ...mapGetters({
      alert: 'alertPanelAlert'
    }),
    alertReady () {
      return this.alert?.details !== 'undefined'
    },
    immediate () {
      const parts = {
        urgency: null,
        response: null,
        statement: null
      }
      // For now, only concerned with immediate urgency
      if (this.alert?.details?.urgency?.toLowerCase?.() === 'immediate') {
        parts.urgency = 'Immediately'
        if (this.alert?.details?.response) {
          parts.response = this.alert?.details?.response
        } else {
          parts.statement = 'Immediate Action Required'
        }
      }
      return parts
    },
    timely () {
      // Render phrases like:
      // Active until further notice
      // Active until 5:00PM CDT
      // Active until tomorrow at 5:00PM CDT
      // Active until Friday at 5:00PM CDT
      // Active until Aug 29 at 5:00PM CDT
      // Today at 5:00PM CDT
      // Tomorrow at 5:00PM CDT
      // Friday at 5:00PM CDT
      // Alert has expired
      return this.generateTimely(this.start, this.end)
    },
    end () {
      if (this.alert.details.ends !== null) {
        return this.convertLocalToSystemZone(this.alert.details.ends)
      } else if (this.alert.details.vtec === null) {
        return this.convertLocalToSystemZone(this.alert.details.expires)
      }
      return 'until further notice'
    },
    start () {
      if (this.alert?.details?.onset !== null) {
        return this.convertLocalToSystemZone(this.alert.details.onset)
      }
      return this.convertLocalToSystemZone(this.alert.details.effective)
    },
    status () {
      // Render phrases like:
      // A severe event that will likely develop
      // A moderate event that has been observed
      return this.generateStatus(this.alert.details?.severity, this.alert.details?.certainty)
    }
  },
  watch: {
    alert: {
      handler () {
        console.log('Alert changed:', this.alert)
        this.styles.title = {
          borderLeft: `6px solid ${this.alert?.options?.color}`
        }
      },
      immediate: true
    }
  },
  // handles panel template close button
  beforeDestroy () {
    this.clearAlert()
  },
  methods: {
    ...mapActions([
      'setAlertForPanel',
      'tracker'
    ]),
    clearAlert () {
      this.setAlertForPanel(null)
    },
    // because DateTime comes from cmi-radar which uses luxon 1.x and "Localzone" was renamed to "SystemZone" in 2.x
    convertLocalToSystemZone (dateTime) {
      if (!dateTime || !dateTime.isValid) return dateTime
      return DateTime.fromISO(dateTime.toISO(), { zone: 'UTC' }).toLocal()
    },
    generateTimely (start, end) {
      const reasoning = []
      // Get the current time to compare against.
      const now = DateTime.now()

      // 1. Handle invalid end date: If the end date is not valid, the event is ongoing indefinitely.
      if (!end?.isValid) {
        reasoning.push('no end date')
        console.log('Alert phrase reasoning:', reasoning.join('; '))
        return 'Active until further notice'
      }

      // 2. Handle expired events: If the current time is past the end time.
      if (now > end) {
        reasoning.push('event expired')
        console.log('Alert phrase reasoning:', reasoning.join('; '))
        return 'Alert has expired'
      }

      const isActive = now >= start
      const targetDate = DateTime.fromISO(isActive ? end.toISO() : start.toISO(), { zone: 'UTC' }).toLocal()
      reasoning.push(`target date is ${isActive ? 'end' : 'start'} date (${targetDate.toISO()})`)

      // Format the time part of the string (e.g., "5:00pm")
      const formattedTime = targetDate.toFormat('h:mma\xa0ZZZZ')

      // Determine the relative day part of the string (e.g., "today", "tomorrow", "Friday")
      let dayString = ''
      if (targetDate.hasSame(now, 'day')) {
        // If the event is today
        reasoning.push('event is today')
        if (isActive) {
          // For active events ending today, we don't need to say "today".
          reasoning.push('active event ending today')
          dayString = ''
        } else {
          // For events starting later today.
          reasoning.push('upcoming event starting today')
          dayString = 'today'
        }
      } else if (targetDate.day === now.plus({ days: 1 }).day) {
        // If the event is tomorrow
        reasoning.push(`event is tomorrow`)
        dayString = 'tomorrow'
      } else if (targetDate.diff(now, 'days').days < 7 && targetDate > now) {
        // If the event is within the next 7 days (but not today or tomorrow)
        reasoning.push('event is within the next week')
        dayString = targetDate.toFormat('cccc') // 'Friday', 'Monday', etc.
      } else {
        // For events further in the future, use a more specific format.
        reasoning.push('event is beyond next week')
        dayString = targetDate.toFormat('MMM d')
      }

      // 3. Construct the final string based on whether the event is active or upcoming.
      let final
      if (isActive) {
        reasoning.push('event is currently active')
        if (dayString) {
          reasoning.push('has day string')
          final = `Active until ${dayString} at ${formattedTime}`
        } else {
          reasoning.push('no day string')
          final = `Active until ${formattedTime}`
        }
      } else {
        reasoning.push('event is upcoming')
        final = `Starts ${dayString} at ${formattedTime}`
      }
      console.log('Alert phrase reasoning:', reasoning.join('; '))
      return final
    },
    prettyPrint (text) {
      if (!text) return ''
      return text.replace(/(?<![\n.{3}])\n(?![\n-.])/g, ' ')
    },
    generateStatus (severity, certainty) {
      if ((!severity || severity === 'Unknown') && (!certainty || certainty === 'Unknown')) {
        return null
      }
      const parts = {
        start: null,
        severity: null,
        transition: null,
        certainty: null,
        end: null
      }

      // Handle the severity part of the string
      if (severity !== 'Unknown') {
        // Check if the first letter of severity is a vowel to determine the article
        parts.start = severity.match(/^[aeiou]/i) ? 'An' : 'A'
        parts.severity = severity.toLowerCase()
      } else {
        // If severity is unknown, we start with "An event"
        parts.start = 'An'
      }

      // Handle the certainty part of the string
      if (certainty !== 'Unknown') {
        if (certainty === 'Observed') {
          parts.transition = 'has been'
          parts.certainty = 'observed'
        } else if (certainty === 'Possible') {
          parts.transition = 'is'
          parts.certainty = 'possible'
          parts.end = 'to develop'
        } else {
          parts.transition = 'will'
          parts.certainty = certainty.toLowerCase()
          parts.end = 'develop'
        }
      }

      // Join the parts with a space and add a period at the end
      return parts
    },
    async copyTextToClipboard (text, name) {
      this.tracker('event', {
        action: 'alert-copy',
        label: name
      })
      try {
        await navigator.clipboard.writeText(text)
        window.alert(`${name} copied to clipboard!`)
      } catch (err) {
        window.alert(`Failed to copy ${name}: `, err)
      }
    },
    capitalizeFirstLetter (str) {
      if (str.length === 0) {
        return ''
      }
      return str.replace(/^./, char => char.toUpperCase())
    },
    openInApi (url) {
      this.tracker('event', {
        action: 'alert-api',
        label: 'open'
      })
      if (url) {
        window.open(url, '_blank')
      } else {
        window.alert('No URL available to open in API.')
      }
    },
    toggleAdvancedPanel (panel) {
      this.tracker('event', {
        action: 'alert-panel',
        label: panel
      })
      if (this.advancedPanel === panel) {
        this.advancedPanel = null
      } else {
        this.advancedPanel = panel
      }
    },
    toggleTerm (term) {
      if (this.term === term) {
        this.term = null
      } else {
        this.term = term
        this.tracker('event', {
          action: 'alert-term',
          label: 'term'
        })
      }
    }
  }
}
</script>

<style scoped>
.title {
  padding: 16px 16px 16px 10px;
  font-weight: bold;
  font-size: 1.2em;
  background: #efefef;
  color: #333;
}

.immediate {
  font-size: 140%;
  background: #000;
  color: #fff;
  padding: 5px 0 5px 16px;
  font-weight: bold;
}

.immediate-text {
  font-style: italic;
}

.immediate-icon {
  align-self: center;
  font-size: 44%;
}

.body {
  margin-top: 22px;
  padding: 0 16px;
}

.id {
  padding: 4px 0;
  color: #333;
  font-size: 90%;
  margin: 11px 0 22px;
}

.status {
  font-size: 90%;
  font-weight: normal;
  margin-top: 5px;
}

.urgency {
  font-weight: bold;
}

.timely {
  font-size: 120%;
}

.sender {
  margin-top: 22px;
}

.instruction {
  margin-top: 22px;
}

.description {
  margin-top: 22px;
}

.preformatted {
  position: relative;
  font-family: monospace;
  white-space: pre-line;
  font-size: 90%;
}

.advanced {
  margin-top: 33px;
  padding: 16px;
  border-top: 1px dashed #aaa;
}

.properties {
  margin-top: 22px;
}

.advanced-buttons {
  display: flex;
  align-items: center;
  margin-top: 11px;
}

.advanced-button {
  background: #efefef;
  padding: 11px 13px;
  border-left: 1px dashed #cdcdcd;
  cursor: pointer;
}

.advanced-button-gap {
  margin-left: 3px;
}

.properties-instruction {
  font-style: italic;
  margin-bottom: 11px;
}

.properties-name {
  cursor: pointer;
}

.properties-value {
  margin-bottom: 11px;
}

.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease-in-out;
}

.scrollable {
  overflow: auto;
  padding-right: 10px;
  border: 1px solid #ccc;
  background: #f9f9f9;
}

.truncate-single-line {
  overflow: hidden;
  text-overflow: ellipsis;
}

.terms-buttons {
  margin-bottom: 31px;
}

.terms-help {
  margin-top: 22px;
}

.term {
  border-bottom: 1px dashed #777;
  cursor: help;
}

.term-inverse {
  border-bottom-color: #ccc;
}

.term-selected {
  background: #333;
  color: #fff;
  padding: 0 4px;
  border-radius: 5px;
}

.term-selected-inverse {
  background: #fff;
  color: #333;
}

.term-grid {
  margin-top: 11px;
  display: grid;
  grid-template-columns: min-content auto;
  grid-template-rows: repeat(5, auto);
  grid-column-gap: 0;
  grid-row-gap: 2px;
  border: 1px solid #ddd;
}

.term-grid > div {
  padding: 5px 11px;
}

.term-name {
  background: #efefef;
}
</style>
